import type { FormApi } from 'final-form';
import type { ChangeEvent, ReactNode } from 'react';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import ButtonGroup from '@swf/components/ButtonGroup/ButtonGroup';
import createForm from '@swf/components/Forms/Form';
import Toggle, { RawToggle } from '@swf/components/Inputs/Toggle';
import Button from '@swf/components/StarsButton/StarsButton';
import Translation from '@swf/components/Translation/Translation';
import { ResolverTag } from '@swf/core/enums';
import useBeforeUnload from '@swf/core/hooks/useBeforeUnload';
import useResolver from '@swf/core/hooks/useResolver';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { externalLink } from '@swf/core/utils';
import { required } from '@swf/core/utils/validators';

import {
  TXTCLI_Accept,
  TXTCLI_IXd_like_to_receive_details_of_ELL,
  TXTCLI_I_have_read_and_understood_the_ELL_5,
  TXTCLI_I_have_read_and_understood_the_ELL_6,
  TXTCLI_I_understand_that_I_can_always_ELL,
  TXTCLI_Select_All,
  TXTWCL_I_have_read_and_understood_the_ELL_3,
  TXTWCL_Kindly_accept_the_new_agreemen_ELL,
  TXTWCL_We_encourage_you_to_take_the_t_ELL,
  TXTWCL_We_have_introduced_amendments__ELL
} from './LoginEulaForm.messages.json';

interface FormValues {
  selectAll: boolean;
  Eula: boolean;
  privacy: boolean;
  marketingMailList: boolean;
}

const Form = createForm<FormValues>();

const LoginEulaForm: React.FC = () => {
  const { push } = useHistory();
  const beforeUnload = useBeforeUnload();
  const mcEula = useResolver(ResolverTag.MCEula);
  const mcPrivacy = useResolver(ResolverTag.MCPrivacy);
  const { euSilentMigration } = useSelector(select.account.get);
  const useMailList = useSelector(select.account.getEulaMarketingEnabled);

  const {
    auth: { signEula, logout }
  } = useDispatch<Dispatch>();

  useEffect(() => {
    beforeUnload(() => logout({}));
  }, [logout, beforeUnload]);

  const handleSelectAll = (form: FormApi<FormValues>) => (e: ChangeEvent<HTMLInputElement>) => {
    if (useMailList) {
      form.change('marketingMailList', e.target.checked);
    }
    form.change('privacy', e.target.checked);
    form.change('Eula', e.target.checked);
  };

  const handleSubmit = async (values: FormValues) => {
    const { marketingMailList } = values;
    if (useMailList && !marketingMailList) {
      push('/auth/login/eula/opt-in');
    } else {
      await signEula({ marketingMailList, useMailList });
      logout({});
    }
  };

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={{
        selectAll: false,
        Eula: false,
        privacy: false,
        marketingMailList: false
      }}
      render={({ form, invalid, submitting, values: { marketingMailList, privacy, Eula } }) => (
        <>
          <p>
            <Translation {...TXTWCL_We_have_introduced_amendments__ELL} />{' '}
            <Translation {...TXTWCL_We_encourage_you_to_take_the_t_ELL} />{' '}
            <Translation {...TXTWCL_Kindly_accept_the_new_agreemen_ELL} />
          </p>
          <RawToggle
            id="selectAll"
            checked={(marketingMailList || !useMailList) && privacy && Eula}
            label={<Translation {...TXTCLI_Select_All} />}
            onChange={handleSelectAll(form)}
            onOffToggle
            type="switch"
          />
          <Toggle
            id="Eula"
            label={{
              ...(!euSilentMigration
                ? TXTCLI_I_have_read_and_understood_the_ELL_5
                : TXTWCL_I_have_read_and_understood_the_ELL_3),
              values: {
                a: externalLink(mcEula),
                i: (msg: ReactNode) => <i>{msg}</i>
              }
            }}
            type="checkbox"
            validators={[required()]}
          />
          <Toggle
            id="privacy"
            label={{
              ...TXTCLI_I_have_read_and_understood_the_ELL_6,
              values: { a: externalLink(mcPrivacy) }
            }}
            type="checkbox"
            validators={[required()]}
          />
          {useMailList && (
            <Toggle
              id="marketingMailList"
              label={[
                TXTCLI_IXd_like_to_receive_details_of_ELL,
                TXTCLI_I_understand_that_I_can_always_ELL
              ]}
              type="checkbox"
            />
          )}
          <ButtonGroup>
            <Button
              block
              type="submit"
              disabled={invalid || submitting}
              translation={TXTCLI_Accept}
            />
          </ButtonGroup>
        </>
      )}
    />
  );
};

export default LoginEulaForm;
